import React from 'react';
import { FaPencilAlt, FaTimes } from 'react-icons/fa';
import { isValidClipTimestamp, isClipRangeValid, CLIP_SEEK_NUDGE_SECONDS } from './clipTimeUtils';

export default function ClipTimestampField({
  label,
  kind = 'start',
  field = 'startTime',
  value,
  pairedValue,
  isActive = false,
  previewTime = null,
  onActivate,
  onSave,
  onCancel,
  onSeek,
  onNudge,
}) {
  const displayTime = isActive ? (previewTime ?? value) : value;
  const hasDisplay = displayTime && isValidClipTimestamp(displayTime);

  const isInvalid =
    isActive &&
    hasDisplay &&
    pairedValue &&
    isValidClipTimestamp(pairedValue) &&
    !isClipRangeValid(
      field === 'startTime' ? displayTime : pairedValue,
      field === 'endTime' ? displayTime : pairedValue,
    );

  const handleStampClick = () => {
    if (!hasDisplay) return;
    onSeek?.(displayTime, field);
  };

  const handleNudge = (deltaSec) => {
    if (!hasDisplay) return;
    onNudge?.(field, deltaSec);
  };

  return (
    <div className={`clip-ts-field clip-ts-field--${kind} ${isActive ? 'clip-ts-field--editing' : ''}`}>
      <span className="clip-ts-field__label">{label}</span>

      <div className="clip-ts-field__controls">
        {hasDisplay && onNudge && (
          <button
            type="button"
            className="clip-ts-nudge"
            onClick={() => handleNudge(-CLIP_SEEK_NUDGE_SECONDS)}
            title={`Seek ${CLIP_SEEK_NUDGE_SECONDS} seconds earlier in the video`}
            aria-label={`${label} seek minus ${CLIP_SEEK_NUDGE_SECONDS} seconds`}
          >
            −{CLIP_SEEK_NUDGE_SECONDS}s
          </button>
        )}

        {hasDisplay ? (
          <button
            type="button"
            className={`clip-ts-stamp ${isActive ? 'clip-ts-stamp--live' : ''} ${isInvalid ? 'clip-ts-stamp--invalid' : ''}`}
            onClick={handleStampClick}
            title={isActive ? 'Live preview — click to jump' : `Jump video to ${label.toLowerCase()}: ${displayTime}`}
          >
            {displayTime}
          </button>
        ) : (
          <span className="clip-ts-stamp clip-ts-stamp--empty">—</span>
        )}

        {hasDisplay && onNudge && (
          <button
            type="button"
            className="clip-ts-nudge"
            onClick={() => handleNudge(CLIP_SEEK_NUDGE_SECONDS)}
            title={`Seek ${CLIP_SEEK_NUDGE_SECONDS} seconds later in the video`}
            aria-label={`${label} seek plus ${CLIP_SEEK_NUDGE_SECONDS} seconds`}
          >
            +{CLIP_SEEK_NUDGE_SECONDS}s
          </button>
        )}

        {isActive && (
          <button
            type="button"
            className="clip-ts-field__cancel-btn"
            onClick={onCancel}
            title="Cancel editing"
            aria-label={`Cancel editing ${label}`}
          >
            <FaTimes aria-hidden="true" />
          </button>
        )}

        <button
          type="button"
          className={`clip-ts-field__edit-btn ${isActive ? 'clip-ts-field__edit-btn--active' : ''}`}
          onClick={isActive ? onSave : onActivate}
          disabled={isActive && isInvalid}
          title={isActive ? 'Save timestamp' : `Edit ${label.toLowerCase()} from video playhead`}
          aria-label={isActive ? `Save ${label}` : `Edit ${label} from playhead`}
          aria-pressed={isActive}
        >
          <FaPencilAlt aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
